import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Subscription.css';
import axios from 'axios';
import API_BASE_URL from '../config';

const Subscription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [user, setUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [isOnboardingPayment, setIsOnboardingPayment] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);

  // Available plans
  const plans = [
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: 999,
      originalPrice: 1499,
      discount: '33%',
      duration: 'month',
      features: [
        '✅ All HR features included',
        '✅ Unlimited candidates',
        '✅ Priority email support',
        '✅ Basic analytics dashboard',
        '✅ Up to 10 active job postings'
      ],
      popular: false
    },
    {
      id: 'quarterly',
      name: 'Quarterly Plan',
      price: 2699,
      originalPrice: 4497,
      discount: '40%',
      duration: '3 months',
      features: [
        '✅ Everything in Monthly',
        '✅ Advanced candidate filtering',
        '✅ Priority phone support',
        '✅ Advanced analytics',
        '✅ Up to 25 active job postings',
        '✅ Custom onboarding workflows'
      ],
      popular: true
    },
    {
      id: 'yearly',
      name: 'Yearly Plan',
      price: 8999,
      originalPrice: 17988,
      discount: '50%',
      duration: 'year',
      features: [
        '✅ Everything in Quarterly',
        '✅ Dedicated account manager',
        '✅ 24/7 phone support',
        '✅ AI-powered candidate matching',
        '✅ Unlimited job postings',
        '✅ White-label reports',
        '✅ API access'
      ],
      popular: false
    }
  ];

  useEffect(() => {
    loadSubscriptionData();
    checkOnboardingData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('authToken');
      const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
      const subscriptionData = JSON.parse(sessionStorage.getItem('subscription') || '{}');
      
      setUser(userData);
      
      if (subscriptionData && subscriptionData.userId) {
        setSubscription(subscriptionData);
      } else {
        const response = await axios.get(`${API_BASE_URL}/api/hr/subscription-status`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          setSubscription(response.data.data);
          sessionStorage.setItem('subscription', JSON.stringify(response.data.data));
        }
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkOnboardingData = () => {
    // Check if user is coming from onboarding process
    const onboardingData = JSON.parse(sessionStorage.getItem('onboardingData') || '{}');
    const fromOnboarding = sessionStorage.getItem('fromOnboarding') === 'true';
    
    if (fromOnboarding && onboardingData.candidateEmail) {
      setIsOnboardingPayment(true);
      setOnboardingData(onboardingData);
      
      // Show onboarding-specific message
      console.log('🔍 Onboarding payment detected:', onboardingData);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    
    if (isOnboardingPayment && onboardingData) {
      // For onboarding payment, navigate directly to onboarding payment page
      navigate('/onboarding-payment', {
        state: {
          plan: plan,
          onboardingData: onboardingData
        }
      });
    } else {
      // For regular subscription, show payment modal
      setShowPaymentModal(true);
    }
  };

  const handleOnboardingPayment = () => {
    if (!selectedPlan || !onboardingData) return;
    
    // Navigate to onboarding payment page with all data
    navigate('/onboarding-payment', {
      state: {
        plan: selectedPlan,
        onboardingData: onboardingData,
        userEmail: user?.email,
        companyName: user?.company
      }
    });
  };

  const handlePayPalPayment = async () => {
    if (!selectedPlan || !user) return;

    try {
      setPaypalLoading(true);
      setPaymentStatus('Creating PayPal order...');

      const token = sessionStorage.getItem('authToken');
      
      // Create PayPal order
      const response = await axios.post(
        `${API_BASE_URL}/api/payments/create-paypal-order`,
        {
          planId: selectedPlan.id,
          userEmail: user.email,
          paymentMethod: 'paypal'
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const { approvalUrl, orderId, paymentId } = response.data.data;
        
        // Store payment info in session
        sessionStorage.setItem('pendingPayment', JSON.stringify({
          orderId,
          paymentId,
          plan: selectedPlan,
          userEmail: user.email
        }));

        // Open PayPal in new window
        const paymentWindow = window.open(approvalUrl, 'PayPalPayment', 'width=500,height=600');
        
        if (!paymentWindow) {
          alert('Please allow popups for PayPal payment');
          return;
        }

        // Check payment status periodically
        const checkInterval = setInterval(async () => {
          try {
            if (paymentWindow.closed) {
              clearInterval(checkInterval);
              
              // Verify payment
              const verifyResponse = await axios.post(
                `${API_BASE_URL}/api/payments/capture-paypal-order`,
                {
                  orderID: orderId,
                  paymentId: paymentId,
                  userEmail: user.email
                },
                {
                  headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                }
              );

              if (verifyResponse.data.success) {
                setPaymentStatus('Payment successful!');
                
                // Update subscription
                const updatedSubscription = {
                  ...subscription,
                  isPaid: true,
                  trialEnded: false,
                  paymentStatus: 'active',
                  planType: selectedPlan.id,
                  paidStartDate: new Date(),
                  subscriptionEndDate: new Date(Date.now() + (selectedPlan.id === 'yearly' ? 365 : selectedPlan.id === 'quarterly' ? 90 : 30) * 24 * 60 * 60 * 1000)
                };
                
                setSubscription(updatedSubscription);
                sessionStorage.setItem('subscription', JSON.stringify(updatedSubscription));
                
                // Clear pending payment
                sessionStorage.removeItem('pendingPayment');
                
                // If this was for onboarding, redirect to onboarding
                if (isOnboardingPayment) {
                  setTimeout(() => {
                    navigate('/onboarding', {
                      state: {
                        onboardingData: onboardingData,
                        paymentCompleted: true
                      }
                    });
                  }, 2000);
                } else {
                  // Regular subscription success
                  setTimeout(() => {
                    setShowPaymentModal(false);
                    setSelectedPlan(null);
                    setPaymentStatus('');
                    alert('🎉 Payment successful! Your subscription is now active.');
                  }, 2000);
                }
              } else {
                setPaymentStatus('Payment verification failed');
              }
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            clearInterval(checkInterval);
            setPaymentStatus('Payment verification error');
          }
        }, 2000);
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      setPaymentStatus('Payment failed. Please try again.');
    } finally {
      setPaypalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;

    try {
      const token = sessionStorage.getItem('authToken');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/hr/cancel-subscription`,
        {},
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const updatedSubscription = {
          ...subscription,
          isPaid: false,
          paymentStatus: 'cancelled',
          subscriptionEndDate: new Date()
        };
        
        setSubscription(updatedSubscription);
        sessionStorage.setItem('subscription', JSON.stringify(updatedSubscription));
        
        alert('Subscription cancelled successfully. You can continue using until the end of your billing period.');
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      alert('Failed to cancel subscription');
    }
  };

  const handleGoBackToOnboarding = () => {
    navigate('/onboarding', {
      state: {
        onboardingData: onboardingData
      }
    });
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <div className="spinner"></div>
        <p>Loading subscription details...</p>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      <div className="subscription-header">
        <h1>Subscription Management</h1>
        <p>Choose the plan that's right for your HR needs</p>
        
        {/* Onboarding Notice */}
        {isOnboardingPayment && onboardingData && (
          <div className="onboarding-notice">
            <div className="onboarding-notice-icon">🚀</div>
            <div className="onboarding-notice-content">
              <h4>Onboarding Payment Required</h4>
              <p>
                You're setting up onboarding for <strong>{onboardingData.candidateName}</strong> 
                as <strong>{onboardingData.position}</strong>
              </p>
              <p className="onboarding-note">
                Select a plan to proceed with the onboarding payment.
              </p>
              <button 
                className="back-to-onboarding-btn"
                onClick={handleGoBackToOnboarding}
              >
                ← Back to Onboarding
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Current Subscription Status */}
      <div className="current-subscription-card">
        <h3>Current Status</h3>
        <div className="status-details">
          {subscription?.isTrialActive ? (
            <div className="status-trial">
              <span className="status-icon">🚀</span>
              <div>
                <h4>Free Trial Active</h4>
                <p>{subscription.remainingTrialDays} days remaining</p>
                <p className="status-note">Upgrade to unlock all features</p>
              </div>
            </div>
          ) : subscription?.isPaid ? (
            <div className="status-paid">
              <span className="status-icon">✅</span>
              <div>
                <h4>Active Subscription</h4>
                <p>{subscription.planType?.toUpperCase()} Plan • Renews in {subscription.remainingSubscriptionDays} days</p>
                <p className="status-note">Next billing: {new Date(subscription.subscriptionEndDate).toLocaleDateString()}</p>
              </div>
              <button 
                className="cancel-btn"
                onClick={handleCancelSubscription}
              >
                Cancel Subscription
              </button>
            </div>
          ) : subscription?.trialEnded && !subscription?.isPaid ? (
            <div className="status-ended">
              <span className="status-icon">⚠️</span>
              <div>
                <h4>Trial Ended</h4>
                <p>Your free trial has ended. Upgrade to continue using HR Copilot.</p>
              </div>
            </div>
          ) : (
            <div className="status-none">
              <span className="status-icon">📝</span>
              <div>
                <h4>No Active Subscription</h4>
                <p>Choose a plan to get started</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Available Plans */}
      <div className="plans-section">
        <h2>Choose Your Plan</h2>
        <p className="plans-subtitle">
          {isOnboardingPayment 
            ? "Select a plan to proceed with onboarding payment" 
            : "All plans include our core HR features"}
        </p>
        
        <div className="plans-grid">
          {plans.map((plan) => (
            <div 
              key={plan.id} 
              className={`plan-card ${plan.popular ? 'popular' : ''} ${subscription?.planType === plan.id ? 'current' : ''}`}
            >
              {plan.popular && (
                <div className="popular-badge">MOST POPULAR</div>
              )}
              
              {subscription?.planType === plan.id && (
                <div className="current-badge">CURRENT PLAN</div>
              )}
              
              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="currency">₹</span>
                  <span className="amount">{plan.price}</span>
                  <span className="duration">/{plan.duration}</span>
                </div>
                
                {plan.originalPrice && (
                  <div className="plan-discount">
                    <span className="original-price">₹{plan.originalPrice}</span>
                    <span className="discount-percent">{plan.discount} OFF</span>
                  </div>
                )}
              </div>
              
              <div className="plan-features">
                {plan.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    {feature}
                  </div>
                ))}
                
                {/* Onboarding specific feature */}
                {isOnboardingPayment && (
                  <div className="feature-item onboarding-feature">
                    ✅ Includes onboarding for {onboardingData?.candidateName || 'candidate'}
                  </div>
                )}
              </div>
              
              <button 
                className={`plan-select-btn ${subscription?.planType === plan.id ? 'current-btn' : ''} ${isOnboardingPayment ? 'onboarding-btn' : ''}`}
                onClick={() => handlePlanSelect(plan)}
                disabled={subscription?.planType === plan.id}
              >
                {subscription?.planType === plan.id 
                  ? 'Current Plan' 
                  : isOnboardingPayment 
                    ? 'Pay for Onboarding' 
                    : 'Select Plan'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Modal (for regular subscriptions) */}
      {showPaymentModal && selectedPlan && !isOnboardingPayment && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            <div className="payment-modal-header">
              <h3>Complete Payment</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                  setPaymentStatus('');
                }}
              >
                ×
              </button>
            </div>
            
            <div className="payment-modal-content">
              <div className="order-summary">
                <h4>Order Summary</h4>
                <div className="summary-details">
                  <div className="summary-item">
                    <span>Plan:</span>
                    <span>{selectedPlan.name}</span>
                  </div>
                  <div className="summary-item">
                    <span>Duration:</span>
                    <span>{selectedPlan.duration}</span>
                  </div>
                  <div className="summary-item">
                    <span>Amount:</span>
                    <span className="amount-total">₹{selectedPlan.price}</span>
                  </div>
                  {selectedPlan.originalPrice && (
                    <div className="summary-item discount">
                      <span>You save:</span>
                      <span>₹{selectedPlan.originalPrice - selectedPlan.price}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="payment-methods">
                <h4>Select Payment Method</h4>
                
                <div className="method-options">
                  <button 
                    className="method-btn paypal-btn"
                    onClick={handlePayPalPayment}
                    disabled={paypalLoading}
                  >
                    {paypalLoading ? 'Processing...' : (
                      <>
                        <span className="method-icon">🔵</span>
                        Pay with PayPal
                      </>
                    )}
                  </button>
                  
                  <button 
                    className="method-btn card-btn"
                    onClick={() => alert('Credit/Debit card payment coming soon!')}
                  >
                    <span className="method-icon">💳</span>
                    Credit/Debit Card
                  </button>
                </div>
              </div>
              
              {paymentStatus && (
                <div className="payment-status">
                  <p>{paymentStatus}</p>
                </div>
              )}
              
              <div className="payment-security">
                <p className="security-note">
                  🔒 Secure payment • 256-bit SSL encryption • Money-back guarantee
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Can I change plans later?</h4>
            <p>Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated.</p>
          </div>
          <div className="faq-item">
            <h4>Is there a contract?</h4>
            <p>No contracts. Cancel anytime. Monthly and quarterly plans are month-to-month.</p>
          </div>
          <div className="faq-item">
            <h4>What payment methods do you accept?</h4>
            <p>We accept PayPal, UPI, and all major credit/debit cards.</p>
          </div>
          <div className="faq-item">
            <h4>Do you offer refunds?</h4>
            <p>Yes, we offer a 14-day money-back guarantee for annual plans.</p>
          </div>
          {isOnboardingPayment && (
            <div className="faq-item">
              <h4>What happens after onboarding payment?</h4>
              <p>After successful payment, you'll be redirected to complete the candidate's onboarding process.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscription;